import { useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { getProfileCollaboratorInviteCacheKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('useMedicalProfileInvitation', {
    inviteLoadFailure: 'Error loading invitation',
});

export type UseMedicalProfileInvitationArgs = {
    invitationId: string;
    medicalProfileId: string;
};

export type UseMedicalProfileInvitationReturnValue = {
    isLoading: boolean;
    invitation: APITypes.medicalProfileService.CollaboratorInviteListItem | null;
};

export default function useMedicalProfileInvitation({
    invitationId,
    medicalProfileId,
}: UseMedicalProfileInvitationArgs): UseMedicalProfileInvitationReturnValue {
    const medicalProfileInvitationQuery = useQuery({
        queryKey: getProfileCollaboratorInviteCacheKey(medicalProfileId, invitationId),
        queryFn: async () => {
            const response = (await appAPI.get(
                urls.api.collaborators.medicalProfileInvites(medicalProfileId),
            )) as APITypes.medicalProfileService.ListCollaboratorInvitesResponse;
            return (response.invites || []).find((_) => _.medicalProfileCollaboratorInviteId === invitationId);
        },
    });
    useQueryErrorNotify(medicalProfileInvitationQuery, intl('inviteLoadFailure'));

    return {
        isLoading: medicalProfileInvitationQuery.isLoading,
        invitation: medicalProfileInvitationQuery.data || null,
    };
}
